// M004-F0757 (WebUI #200): Apply to account drawer, shared by the Credit notes tab and the member page. Admin only (the caller gates it).
// Review step, then a confirm step that states the amount; the Ledger's refusal is shown verbatim. One Idempotency-Key per attempt: a
// step-up retry of the SAME body reuses it. Step-up (SAL 4) reuses the platform StepUpDialog and getStepUpChallengeFromError.
import React, { useEffect, useRef, useState } from 'react';
import { creditAllocationApi, relayPayloadOf } from '../../services/creditAllocationApi';
import { classifyRelayOutcome } from '../../services/relayOutcome';
import StepUpDialog from '../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import { money } from '../invoices/invoiceModel';
import { remainingMinor } from './creditNoteModel';
import {
  amountMinorOf,
  buildApplyToAccountBody,
  confirmText,
  figuresText,
  newIdempotencyKey,
  refusalText,
  successText,
  validateApplyToAccount,
} from './applyToAccountModel';
import './CreditNotesPanel.css';

function FieldError({ message, id }) {
  return message ? <p className="cn-field-error" id={id}>{message}</p> : null;
}

// Features: M004-F0775 (WebUI #248). An unknown outcome is never shown as a failure: the Ledger may have allocated the credit.
const UNCONFIRMED = "We couldn't confirm whether this was allocated to the account. We've refreshed what the Ledger shows — check it before trying again.";
const REFUSED_NO_TEXT = 'The Ledger refused this. Nothing was allocated.';
// A definite refusal: the Ledger's own text (refusalText), or the fallback sentence when the Ledger sent no message at all.
const refusedText = (cause) => {
  const data = cause?.response?.data;
  return data?.error?.message || data?.message ? refusalText(cause) : REFUSED_NO_TEXT;
};

// onApplied receives { message, figures } for the caller's notice. onUnconfirmed (optional) is called on an unknown outcome so the
// caller re-reads what its view shows, as it does after a success; the drawer itself shows no success.
export default function ApplyToAccountDrawer({ note, orgId, onApplied, onClose, onUnconfirmed }) {
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [unconfirmed, setUnconfirmed] = useState(''); // Features: M004-F0775
  const [stepUp, setStepUp] = useState(null);
  const attempt = useRef(null);
  const retry = useRef(null);
  const amountRef = useRef(null);
  // Opening the drawer moves focus to its first field, so a keyboard user lands in the form, not back in the table.
  useEffect(() => { amountRef.current?.focus(); }, []);
  const availableMinor = remainingMinor(note);

  const review = () => {
    const found = validateApplyToAccount({ amount, comment }, { availableMinor });
    setErrors(found);
    setFormError('');
    setConfirming(Object.keys(found).length === 0);
  };

  const attemptApply = (body, key) => async () => {
    const response = await creditAllocationApi.allocateToAccount(orgId, body, key);
    attempt.current = null;
    const data = relayPayloadOf(response)?.data;
    onApplied({ message: successText(note.credit_note_number), figures: figuresText(data) });
  };

  // A write refused for step-up opens the StepUpDialog; on completion the SAME action runs again (same key).
  const gated = async (action) => {
    try {
      await action();
    } catch (cause) {
      const challenge = getStepUpChallengeFromError(cause);
      if (!challenge?.id) throw cause;
      retry.current = action;
      setStepUp(challenge);
    }
  };

  // Features: M004-F0775. An unknown outcome keeps the attempt's key, so the next press re-sends the SAME Idempotency-Key. Only a
  // definite refusal clears the key (a fresh press then starts a new attempt).
  const applyFailed = (cause) => {
    if (classifyRelayOutcome(cause) === 'unknown') {
      setFormError('');
      setUnconfirmed(UNCONFIRMED);
      onUnconfirmed?.();
      return;
    }
    attempt.current = null;
    setFormError(refusedText(cause));
  };

  const submit = async () => {
    const found = validateApplyToAccount({ amount, comment }, { availableMinor });
    setErrors(found);
    if (Object.keys(found).length) { setConfirming(false); return; }
    const body = buildApplyToAccountBody({ creditNoteId: note.credit_note_id, amount, comment });
    const fingerprint = JSON.stringify(body);
    if (!attempt.current || attempt.current.fingerprint !== fingerprint) attempt.current = { fingerprint, key: newIdempotencyKey() };
    setSaving(true);
    setFormError('');
    setUnconfirmed('');
    try {
      await gated(attemptApply(body, attempt.current.key));
    } catch (cause) {
      applyFailed(cause);
    } finally {
      setSaving(false);
    }
  };

  const finishStepUp = async () => {
    const action = retry.current;
    retry.current = null;
    setStepUp(null);
    if (!action) return;
    setSaving(true);
    setUnconfirmed('');
    try {
      await action();
    } catch (cause) {
      applyFailed(cause);
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (confirming) submit(); else review();
  };

  const changeAmount = (event) => { setAmount(event.target.value); setConfirming(false); };
  const changeComment = (event) => { setComment(event.target.value); setConfirming(false); };

  return (
    <>
      <StepUpDialog
        challenge={stepUp}
        title="Verify credit change"
        description="Confirm your identity before changing this member's credit."
        onComplete={finishStepUp}
        onCancel={() => { retry.current = null; setStepUp(null); setFormError('Verification was cancelled.'); }}
      />
      <form className="cn-drawer cn-drawer-form" role="dialog" aria-modal="false" aria-label="Apply to account" data-testid="cn-account-drawer" onSubmit={onSubmit} noValidate>
        <header className="cn-drawer-head"><h3>Apply to account</h3><button type="button" onClick={onClose} aria-label="Close">×</button></header>
        <div className="cn-form">
          <p className="cn-muted">Moves unused credit from {note.credit_note_number} to the customer&apos;s account. Remaining on the note: <strong data-testid="cn-account-remaining">{money(availableMinor, note.currency)}</strong>.</p>
          <label>Amount (£)
            <input ref={amountRef} type="number" min="0.01" step="0.01" data-testid="cn-account-amount" value={amount} onChange={changeAmount} aria-invalid={Boolean(errors.amount)} aria-describedby="cn-account-amount-err" />
          </label>
          <FieldError id="cn-account-amount-err" message={errors.amount} />
          <label>Comment<textarea data-testid="cn-account-comment" value={comment} onChange={changeComment} aria-invalid={Boolean(errors.comment)} aria-describedby="cn-account-comment-err" /></label>
          <FieldError id="cn-account-comment-err" message={errors.comment} />
          {confirming ? <p className="cn-notice" role="status" data-testid="cn-account-confirm">{confirmText(amountMinorOf(amount))}</p> : null}
          {formError ? <p className="cn-form-error" role="alert" data-testid="cn-account-error">{formError}</p> : null}
          {unconfirmed ? <p className="cn-notice" role="status" data-testid="cn-account-unconfirmed">{unconfirmed}</p> : null}
        </div>
        <footer className="cn-drawer-foot">
          <button type="button" onClick={onClose}>Cancel</button>
          {confirming ? (
            <button type="submit" className="cn-primary" data-testid="cn-account-save" disabled={saving}>{saving ? 'Applying…' : 'Confirm apply to account'}</button>
          ) : (
            <button type="submit" className="cn-primary" data-testid="cn-account-review">Review</button>
          )}
        </footer>
      </form>
    </>
  );
}
